/**
 * Filtering a list that is already on screen.
 *
 * Five surfaces needed searching and they do not all need the same thing.
 * Read searches 2,514 clauses across four instruments and has to go to the
 * database. Claims, Protect, Compare and the ledgers are looking at fifteen,
 * thirty-three, five hundred and eighty rows that are already loaded — sending
 * a query to the server to filter rows the browser is holding would add a round
 * trip to every keystroke and be slower than doing nothing.
 *
 * So this is client-side and deliberately dumb. Every term has to appear
 * somewhere in the row's text, case-insensitively, in any order. No fuzzy
 * matching, no ranking, no stemming.
 *
 * Dumb is the feature. A filter box is used while looking at the list, and its
 * value is that the relationship between what you typed and what remains is
 * obvious. Fuzzy matching means a row survives for reasons the reader cannot
 * see, which in a list of bargaining claims reads as the software having an
 * opinion. Ranking means the list reorders under the cursor. The one thing this
 * has to do is disappear rows that plainly do not match, and it does that.
 */

import { useMemo, useState } from 'react';

/**
 * Does this row match?
 *
 * Exported and tested separately from the component, because the matching rule
 * is the part that can be wrong in ways nobody notices — a row that quietly
 * fails to match is invisible by definition.
 */
export function matches(fields: readonly (string | null | undefined)[], query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = fields.filter(Boolean).join(' ').toLowerCase();
  return terms.every((term) => hay.includes(term));
}

/**
 * The box, and what it says about what it did.
 *
 * `total` and `shown` are both required, because a filter that hides rows
 * without saying how many is how somebody concludes there are three claims when
 * there are fifteen. The count is the honest part of the control.
 */
export function FilterBox({
  label,
  value,
  onChange,
  total,
  shown,
  noun,
}: {
  /** What is being filtered, for the screen reader and for the placeholder. */
  label: string;
  value: string;
  onChange: (next: string) => void;
  total: number;
  shown: number;
  /** Plural noun for the count line — "claims", "cards", "comparisons". */
  noun: string;
}) {
  const filtering = value.trim().length > 0;
  return (
    <div className="row-flush mb-3 gap-2">
      <input
        type="search"
        className="min-w-0 flex-1 border-b border-[var(--color-rule)] bg-transparent py-1 text-[13px]"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {/*
        The count only appears while filtering. Shown permanently it is noise on
        a screen that already carries a heading with a number in it; shown while
        filtering it is the only thing telling a reader that what they are
        looking at is a subset.
      */}
      {filtering && (
        <span className="label shrink-0 text-[11px] text-[var(--color-muted)]" role="status">
          {shown} of {total} {noun}
        </span>
      )}
      {filtering && (
        <button
          type="button"
          className="label shrink-0 text-[11px] text-[var(--color-muted)] underline"
          onClick={() => onChange('')}
        >
          Clear
        </button>
      )}
    </div>
  );
}

/**
 * The state and the filtered rows together, so a surface wires this in three
 * lines rather than six.
 *
 * `fieldsOf` names what is searchable per row, and naming it at the call site
 * rather than stringifying the whole object is deliberate: a claim's id and its
 * owner's colour token would otherwise be matchable, so typing a hex code would
 * filter the list and typing a word might match a uuid.
 */
export function useFilter<T>(
  rows: readonly T[],
  fieldsOf: (row: T) => readonly (string | null | undefined)[],
): {
  query: string;
  setQuery: (next: string) => void;
  filtered: T[];
  total: number;
} {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => rows.filter((row) => matches(fieldsOf(row), query)),
    // fieldsOf is a literal at every call site; including it would rebuild the
    // list on every render for no benefit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, query],
  );
  return { query, setQuery, filtered, total: rows.length };
}
